import { observer } from 'mobx-react';
import { FC, useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { ControlledTable } from '@monorepo/controlled/src/components/table-wrapper/table-wrapper';
import { useJobActions } from '../../../modules/hooks/actions/job.actions';
import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { useJobHeaders } from '../../../modules/hooks/theaders/job.headers';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { IAdminxStore } from '../../../modules/stores';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { useRoute } from '../../../modules/hooks/tools/use-route';
import {
	getFilterObject,
	groupByMapper,
	IPerformanceOptionsDateSegments,
	IPerformanceOptionsSortOrder,
	PerformancesColumnsSnakeCase,
	useReportsList,
} from '../../../modules/hooks/apis/use-reports-list';
import { usePageSettings } from '@monorepo/tools/src/lib/hooks/tools/use-page-settings';
import styles from './jobs.page.module.scss';
import { JobModel } from '../../../modules/models/job.model';
import { useList } from '../../../modules/hooks/tools/use-list';

const pageBarDebugProps = { dataAttrs: [new DataAttribute('id', 'jobs_pagebar')] };
const tableDebugProps = { dataAttrs: [new DataAttribute('id', 'jobs_table')] };
const actionsDebugProps = { dataAttrs: [new DataAttribute('id', 'jobs_actions')] };

export const Jobs: FC = observer(() => {
	const { settingsStore, jobStore, toastsStore } = useStores<IAdminxStore>();
	const { currentRouteWithoutSpecialChars } = useRoute();
	const jobListStore = jobStore.getListStore();
	const noReportJobsStore = jobStore.getJobsListStore();
	const [noReportsData, setNoReportsData] = useState<JobModel[]>([]);
	const [showRunStats, setShowRunStats] = useState(false);
	const tableStore = settingsStore.pages.get(currentRouteWithoutSpecialChars)?.tableStore;

	const columnVisibility = usePageSettings().tableStore?.getColumnVisibility();
	const defaultSortBy = usePageSettings().tableStore?.getSortingBy();

	const filters = tableStore?.getFiltersStore(currentRouteWithoutSpecialChars)?.getCurrentFilters();
	const filtersArray = getFilterObject(filters || new Map(), 'adxJob');
	const currSortBy = tableStore?.getSortingBy();

	useEffect(() => {
		let data = [...noReportJobsStore.getList()];
		if (filtersArray.length > 0) {
			const numberFilters = ['max_retries', 'retry_timeout', 'refresh_rate'];
			const stringFilters = ['name', 'task_classes', 'comment'];
			const enumFilters = ['is_active', 'run_on_init', 'status'];

			filtersArray.forEach(filter => {
				data = data.filter((job: JobModel) => {
					const camelCaseColumn = filter.column.replace(/_([a-z])/g, g => g[1].toUpperCase());
					const jobValue = (job as any)[camelCaseColumn];
					// 🔢 Number filters
					if (numberFilters.includes(filter.column)) {
						const value = Number(filter.value[0]);
						const jobNum = Number(jobValue);

						switch (filter.filterType) {
							case 'EQUALS':
								return jobNum === value;
							case 'GREATER':
								return jobNum > value;
							case 'LESSER':
								return jobNum < value;
							case 'LESSER_EQUALS':
								return jobNum <= value;
							case 'GREATER_EQUALS':
								return jobNum >= value;
							default:
								return true;
						}
					}

					// 🔘 Enum / boolean filters
					if (enumFilters.includes(filter.column)) {
						const value = Boolean(parseInt(filter.value[0]));
						return filter.inverted ? jobValue !== value : jobValue === value;
					}

					// 🔤 String filters
					if (stringFilters.includes(filter.column)) {
						// const values = filter.value.map(String);
						console.log('Applying string filter:', filter.filterType, filter.inverted);
						switch (filter.filterType) {
							case 'CONTAINS':
								if (filter.inverted) {
									return !String(jobValue).toLowerCase().includes(String(filter.value[0]).toLowerCase());
								}
								return String(jobValue).toLowerCase().includes(String(filter.value[0]).toLowerCase());
							case 'EQUALS':
								if (filter.inverted) {
									return String(jobValue).toLowerCase() !== String(filter.value[0]).toLowerCase();
								}
								return String(jobValue).toLowerCase() === String(filter.value[0]).toLowerCase();
							default:
								return true;
						}
					}
					return true;
				});
			});
		}
		data.sort((a, b) => {
			if (!currSortBy || currSortBy.length === 0) {
				return 0;
			}
			const sort = currSortBy[0];
			const camelCaseColumn = sort.id.replace(/_([a-z])/g, g => g[1].toUpperCase());
			const aValue = (a as any)[camelCaseColumn];
			const bValue = (b as any)[camelCaseColumn];

			if (aValue < bValue) {
				return sort.desc ? 1 : -1;
			}
			if (aValue > bValue) {
				return sort.desc ? -1 : 1;
			}
			return 0;
		});
		setNoReportsData(data);
	}, [filters, JSON.stringify(filtersArray), noReportJobsStore.getList(), currSortBy]);

	// useEffect(() => {
	// 	console.log('noReportsData changed:', Array.isArray(currSortBy) ? [...currSortBy] : currSortBy);
	// }, [currSortBy]);

	const groupByColumns = Object.keys(columnVisibility || {})
		.map((col: string) => {
			if (typeof col === 'string') {
				return groupByMapper[col];
			}
		})
		.filter(val => !!val)
		.filter(v => {
			if (showRunStats) {
				return true;
			}
			return (
				v !== 'start_time' &&
				v !== 'job_identifier' &&
				v !== 'status' &&
				v !== 'retry_attempts' &&
				v !== 'error_message' &&
				v !== 'end_time' &&
				v !== 'job_duration' &&
				v !== 'next_run_time'
			);
		}) as PerformancesColumnsSnakeCase[];

	const { fetchReports } = useReportsList(
		jobListStore,
		{
			groupBys: [...groupByColumns],
			reportName: 'adxJob',
			sortBy: defaultSortBy?.[0]?.id || 'id',
			sortOrder:
				defaultSortBy?.[0]?.desc !== undefined
					? defaultSortBy[0].desc
						? IPerformanceOptionsSortOrder.Desc
						: IPerformanceOptionsSortOrder.Asc
					: IPerformanceOptionsSortOrder.Desc,
			dateSegment: IPerformanceOptionsDateSegments.None,
			submit: true,
		},
		{ isStopUseEffect: !showRunStats },
		toastsStore
	);

	const { columns } = useJobHeaders(showRunStats);
	const { SelectedActions, IndexActions, FiltersActions } = useJobActions({ debugProps: actionsDebugProps, hasRunInfo: showRunStats });
	const options = useMemo(() => (showRunStats ? undefined : {}), [showRunStats]);
	useList<JobModel>(noReportJobsStore, options);
	useEffect(() => {
		const currVisibility = tableStore?.getColumnVisibility();
		tableStore?.setColumnVisibility({
			...currVisibility,
			startTime: showRunStats,
			jobIdentifier: showRunStats,
			status: showRunStats,
			retryAttempts: showRunStats,
			errorMessage: showRunStats,
			endTime: showRunStats,
			jobDuration: showRunStats,
			nextRunTime: showRunStats,
		});
	}, [showRunStats]);
	const teleportRootId = 'teleported-action-liner';

	return (
		<Page>
			<Pagebar debugProps={pageBarDebugProps}>
				<PagebarTitle>Jobs</PagebarTitle>
			</Pagebar>
			<div className={styles.teleportRoot} id={teleportRootId}>
				<div className={styles.showRunStatsWrapper}>
					<label htmlFor="showRunStats">Run Info</label>
					<input
						id="showRunStats"
						type="checkbox"
						className={styles.showRunStatsBtn}
						checked={showRunStats}
						onChange={() => setShowRunStats(prev => !prev)}
					/>
				</div>
			</div>
			<ControlledTable
				currentRoute={currentRouteWithoutSpecialChars}
				columns={columns}
				defaultSortBy={defaultSortBy?.[0] ? defaultSortBy : [{ id: 'id', desc: true }]}
				debugProps={tableDebugProps}
				data={!showRunStats ? noReportsData : jobListStore.getData()?.data || []}
				isLoading={!showRunStats ? noReportJobsStore.getIsLoading() : jobListStore.getIsLoading()}
				fetchReports={showRunStats ? fetchReports : undefined}
				makeLinerPortable={true}
				skipFilter={!showRunStats}
				teleportRootId={teleportRootId}>
				{() => {
					return {
						SelectedActions,
						IndexActions,
						FiltersActions,
					};
				}}
			</ControlledTable>
			<Outlet />
		</Page>
	);
});
